import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Lock, ArrowRight, X, Users } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

/**
 * GuestBanner — iter194
 *
 * A very short sticky strip that sits under the affiliate promo banner
 * for anonymous visitors, saying: "You're browsing as a guest — sign up
 * free to unlock every feature". Complements the modal + the wall page
 * so anon visitors always see at least one prompt on screen, no matter
 * which route they land on.
 *
 * Auto-hides for signed-in users, on silent routes, and once dismissed
 * for the current session (survives page navigation, resets on browser
 * close). Deliberately session-scoped (not 24h) so a returning visitor
 * still gets the strip next time.
 */
const SESSION_KEY = "ccu:guest-banner:dismissed";
const SILENT_PATHS = ["/admin", "/login", "/register", "/pro", "/billing", "/beta", "/share", "/print", "/launch-pack", "/tag"];

export default function GuestBanner() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [weekCount, setWeekCount] = useState(null);

  useEffect(() => {
    try { setDismissed(sessionStorage.getItem(SESSION_KEY) === "1"); }
    catch { setDismissed(false); }
  }, [location.pathname]);

  // iter205 — soft social-proof: show how many campers signed up this
  // week. Silent on error so the banner never breaks. Cached in
  // sessionStorage for 10 min so we don't hammer the endpoint on every
  // route change.
  useEffect(() => {
    if (user || loading) return;
    const cacheKey = "ccu:signup-count-cache";
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const { ts, week } = JSON.parse(raw);
        if (Date.now() - ts < 10 * 60 * 1000) { setWeekCount(week); return; }
      }
    } catch { /* ignore */ }
    api.get("/stats/signups")
      .then(({ data }) => {
        const w = data?.this_week || 0;
        setWeekCount(w);
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), week: w })); } catch { /* ignore */ }
      })
      .catch(() => { /* silent */ });
  }, [user, loading]);

  if (loading || user || dismissed) return null;
  if (SILENT_PATHS.some((p) => location.pathname.startsWith(p))) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const nextParam = encodeURIComponent(location.pathname + location.search);

  return (
    <div
      className="w-full bg-[#fdfaf6] border-b border-[#eadfd2] text-[#3d3a34]"
      role="region"
      aria-label="Guest browsing notice"
      data-testid="guest-banner"
    >
      <div className="max-w-6xl mx-auto px-3 sm:px-5 py-1.5 flex items-center gap-2 sm:gap-3">
        <Lock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#ff4500] shrink-0" strokeWidth={2.25} />
        <div className="text-[11px] sm:text-[12px] leading-tight flex-1 min-w-0">
          <span className="font-semibold uppercase tracking-wider mr-1 text-[#0e0e0e]">Browsing as guest</span>
          <span className="hidden sm:inline text-[#3d3a34]/85">— free sign-up unlocks prices, the full map, favourites, chat &amp; more.</span>
          <span className="sm:hidden text-[#3d3a34]/85">— free sign-up unlocks everything.</span>
        </div>
        {weekCount != null && weekCount > 0 && (
          <span
            className="hidden md:inline-flex items-center gap-1 text-[10px] mono uppercase tracking-wider text-[#0a7f3f] bg-[#0a7f3f]/10 border border-[#0a7f3f]/25 px-2 py-0.5 shrink-0"
            title={`${weekCount} campers signed up this week`}
            data-testid="guest-banner-signup-count"
          >
            <Users className="w-3 h-3" strokeWidth={2.25} />
            {weekCount} joined this week
          </span>
        )}
        <Link
          to={`/register?next=${nextParam}`}
          className="inline-flex items-center gap-1 bg-white text-[#ff4500] border border-[#ff4500] text-[10px] sm:text-[11px] font-black uppercase tracking-wider px-2.5 sm:px-3 py-1 sm:py-1.5 hover:bg-[#ff4500] hover:text-white transition-colors shrink-0"
          data-testid="guest-banner-signup"
        >
          Sign up <ArrowRight className="w-3 h-3" strokeWidth={2.5} />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss guest banner"
          className="text-[#3d3a34]/50 hover:text-[#3d3a34] p-1 -mr-1 shrink-0"
          data-testid="guest-banner-close"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
